import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import ReactQuill from "react-quill-new";
import "react-quill-new/dist/quill.snow.css";
import { api, formatApiErrorDetail, downloadExport } from "../lib/api";
import { PageHeader, EmptyState, Confirm } from "../components/Shared";
import { TableSkeleton } from "../components/Skeletons";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { SketchPad } from "../components/SketchPad";
import { toast } from "sonner";
import { FileText, Save, Pencil, Trash2, FileDown, X, PenLine } from "lucide-react";

const QUILL_MODULES = {
  toolbar: [[{ header: [1, 2, 3, false] }], ["bold", "italic", "underline", "strike"],
            [{ list: "ordered" }, { list: "bullet" }], [{ align: [] }], ["clean"]],
};
const QUILL_FORMATS = ["header", "bold", "italic", "underline", "strike", "list", "align"];
const blank = () => ({ title: "", recipient: "", recipient_address: "", subject: "", body: "",
  letter_date: new Date().toISOString().slice(0, 10), closing: "Yours faithfully,", sketch: "",
  margin_top: 25.4, margin_bottom: 25.4, margin_left: 25.4, margin_right: 25.4 });

const MARGIN_PRESETS = {
  Normal: { margin_top: 25.4, margin_bottom: 25.4, margin_left: 25.4, margin_right: 25.4 },
  Narrow: { margin_top: 12.7, margin_bottom: 12.7, margin_left: 12.7, margin_right: 12.7 },
  Moderate: { margin_top: 25.4, margin_bottom: 25.4, margin_left: 19.05, margin_right: 19.05 },
  Wide: { margin_top: 25.4, margin_bottom: 25.4, margin_left: 50.8, margin_right: 50.8 },
};
const MARGIN_KEYS = ["margin_top", "margin_bottom", "margin_left", "margin_right"];
const detectPreset = (f) => {
  for (const [name, m] of Object.entries(MARGIN_PRESETS)) {
    if (MARGIN_KEYS.every((k) => Math.abs((f[k] ?? 25.4) - m[k]) < 0.05)) return name;
  }
  return "Custom";
};

const LETTER_TEMPLATES = [
  { key: "blank", label: "Blank letter", subject: "", closing: "Yours faithfully,", body: "" },
  { key: "quotation_cover", label: "Quotation cover letter",
    subject: "Quotation for [Scope of Work]", closing: "Yours faithfully,",
    body: "<p>Dear [Client Name],</p><p>Thank you for the opportunity to quote for the above works. Please find attached our detailed quotation for your kind consideration.</p><p>The quotation covers the following:</p><ul><li>[Item / scope 1]</li><li>[Item / scope 2]</li><li>[Item / scope 3]</li></ul><p>The quoted prices are valid for [30] days from the date of this letter. We remain available to discuss any adjustments to suit your requirements.</p><p>We look forward to working with you.</p>" },
  { key: "meter_inspection", label: "Meter inspection request",
    subject: "Request for Meter Inspection — [Location]", closing: "Yours faithfully,",
    body: "<p>Dear Sir/Madam,</p><p>We write to formally request an inspection of the electrical meter installed at [Address / Site], reference [Meter / Account No.].</p><p>The purpose of the inspection is to [state reason — e.g. confirm correct wiring and readings following a new installation].</p><p>Kindly advise on a convenient date and time for your team to carry out the inspection. Our site contact is [Name, Phone].</p><p>Thank you for your cooperation.</p>" },
  { key: "payment_reminder", label: "Payment reminder",
    subject: "Reminder: Outstanding Payment — Invoice [No.]", closing: "Yours faithfully,",
    body: "<p>Dear [Client Name],</p><p>We wish to kindly remind you of the outstanding balance of <strong>[Amount] FCFA</strong> in respect of Invoice [No.] dated [Date], for works carried out at [Site].</p><p>We would be grateful if settlement could be made on or before [Due Date]. Should payment already have been made, please disregard this notice.</p><p>Thank you for your continued partnership.</p>" },
  { key: "completion_notice", label: "Project completion notice",
    subject: "Notice of Completion — [Project Name]", closing: "Yours faithfully,",
    body: "<p>Dear [Client Name],</p><p>We are pleased to inform you that the works for <strong>[Project Name]</strong> at [Site] have been completed in line with the agreed scope and specifications.</p><p>The following works were carried out:</p><ul><li>[Work 1]</li><li>[Work 2]</li></ul><p>We kindly request your team to arrange a joint inspection and handover at your earliest convenience. It has been a pleasure serving you.</p>" },
];

export default function OfficialLetters() {
  const qc = useQueryClient();
  const [form, setForm] = useState(blank());
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [showSketch, setShowSketch] = useState(false);
  const [marginPreset, setMarginPreset] = useState("Normal");

  const applyMarginPreset = (p) => { setMarginPreset(p); if (p !== "Custom") setForm((f) => ({ ...f, ...MARGIN_PRESETS[p] })); };
  const setMargin = (k, cm) => { setForm((f) => ({ ...f, [k]: (parseFloat(cm) || 0) * 10 })); setMarginPreset("Custom"); };

  const { data: letters = [], isLoading } = useQuery({ queryKey: ["letters"], queryFn: () => api.get("/letters").then((r) => r.data) });
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const reset = () => { setForm(blank()); setEditingId(null); setShowSketch(false); setMarginPreset("Normal"); };

  const applyTemplate = (key) => {
    const t = LETTER_TEMPLATES.find((x) => x.key === key);
    if (!t) return;
    setForm((f) => ({ ...f, subject: t.subject, body: t.body, closing: t.closing || f.closing }));
    toast.success(t.key === "blank" ? "Body cleared." : `Loaded the "${t.label}" template — fill in the [bracketed] parts.`);
  };

  const load = (l) => {
    setEditingId(l.id);
    const next = { title: l.title || "", recipient: l.recipient || "", recipient_address: l.recipient_address || "",
      subject: l.subject || "", body: l.body || "", letter_date: (l.letter_date || "").slice(0, 10) || new Date().toISOString().slice(0, 10),
      closing: l.closing || "Yours faithfully,", sketch: l.sketch || "",
      margin_top: l.margin_top ?? 25.4, margin_bottom: l.margin_bottom ?? 25.4, margin_left: l.margin_left ?? 25.4, margin_right: l.margin_right ?? 25.4 };
    setForm(next);
    setShowSketch(!!l.sketch);
    setMarginPreset(detectPreset(next));
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const persist = async () => {
    if (!form.subject.trim()) { toast.error("Add a subject for this letter."); return null; }
    setSaving(true);
    try {
      if (editingId) { await api.put(`/letters/${editingId}`, form); qc.invalidateQueries({ queryKey: ["letters"] }); return editingId; }
      const { data } = await api.post("/letters", form);
      qc.invalidateQueries({ queryKey: ["letters"] });
      return data.id;
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not save."); return null; }
    finally { setSaving(false); }
  };

  const save = async () => { const id = await persist(); if (id) { toast.success(editingId ? "Letter updated." : "Letter saved."); reset(); } };

  const saveAndExport = async () => {
    const id = await persist();
    if (!id) return;
    toast.success("Saved. Opening PDF…");
    const num = letters.find((x) => x.id === id)?.number || "letter";
    await downloadExport(`/export/letter/${id}.pdf`, `${num}.pdf`);
    qc.invalidateQueries({ queryKey: ["letters"] });
    reset();
  };

  const saveAndExportDocx = async () => {
    const id = await persist();
    if (!id) return;
    toast.success("Saved. Downloading Word…");
    const num = letters.find((x) => x.id === id)?.number || "letter";
    await downloadExport(`/export/letter/${id}.docx`, `${num}.docx`);
    qc.invalidateQueries({ queryKey: ["letters"] });
    reset();
  };

  const remove = async (id) => {
    try { await api.delete(`/letters/${id}`); qc.invalidateQueries({ queryKey: ["letters"] }); toast.success("Deleted."); }
    catch { toast.error("Could not delete."); }
  };

  const exportPdf = async (l) => { await downloadExport(`/export/letter/${l.id}.pdf`, `${l.number}.pdf`); qc.invalidateQueries({ queryKey: ["letters"] }); };
  const exportDocx = async (l) => { await downloadExport(`/export/letter/${l.id}.docx`, `${l.number}.docx`); qc.invalidateQueries({ queryKey: ["letters"] }); };
  const downloadTemplate = () => downloadExport("/export/letterhead-template.docx", "letterhead-template.docx");

  return (
    <div className="space-y-6" data-testid="letters-page">
      <PageHeader title="Official Letters" subtitle="Write a letter on the company letterhead, save a copy in the system, and export it to PDF.">
        <Button variant="outline" onClick={downloadTemplate} data-testid="letters-download-template"><FileDown className="h-4 w-4 mr-2" /> Letterhead (Word)</Button>
      </PageHeader>

      <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5 space-y-4" data-testid="letter-editor">
        <div className="flex items-center justify-between">
          <div className="font-heading font-semibold text-slate-800">{editingId ? "Edit letter" : "New letter"}</div>
          {editingId && <Button variant="ghost" size="sm" onClick={reset} data-testid="letter-cancel-edit"><X className="h-4 w-4 mr-1" /> Cancel</Button>}
        </div>
        <div className="space-y-1.5 max-w-sm">
          <Label>Start from a template</Label>
          <Select value="" onValueChange={applyTemplate}>
            <SelectTrigger data-testid="letter-template-select"><SelectValue placeholder="Choose a template…" /></SelectTrigger>
            <SelectContent>
              {LETTER_TEMPLATES.map((t) => <SelectItem key={t.key} value={t.key}>{t.label}</SelectItem>)}
            </SelectContent>
          </Select>
          <p className="text-xs text-slate-400">Prefills the subject and body with a ready-made draft. Replace the [bracketed] placeholders with your details.</p>
        </div>
        <div className="grid md:grid-cols-2 gap-4">
          <div className="space-y-1.5"><Label>Internal title (optional)</Label><Input data-testid="letter-title" value={form.title} onChange={(e) => set("title", e.target.value)} placeholder="For your reference only — not printed" /></div>
          <div className="space-y-1.5"><Label>Date</Label><Input type="date" data-testid="letter-date" value={form.letter_date} onChange={(e) => set("letter_date", e.target.value)} /></div>
          <div className="space-y-1.5"><Label>Recipient</Label><Input data-testid="letter-recipient" value={form.recipient} onChange={(e) => set("recipient", e.target.value)} placeholder="e.g. The Director, ENEO Cameroon" /></div>
          <div className="space-y-1.5"><Label>Recipient address</Label><Textarea rows={2} data-testid="letter-recipient-address" value={form.recipient_address} onChange={(e) => set("recipient_address", e.target.value)} placeholder="One line per row" /></div>
        </div>
        <div className="space-y-1.5"><Label>Subject *</Label><Input data-testid="letter-subject" value={form.subject} onChange={(e) => set("subject", e.target.value)} placeholder="Subject of the letter" /></div>
        <div className="space-y-1.5">
          <Label>Body</Label>
          <div className="stems-quill" data-testid="letter-body">
            <ReactQuill theme="snow" value={form.body} onChange={(v) => set("body", v)} modules={QUILL_MODULES} formats={QUILL_FORMATS} />
          </div>
        </div>
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <Label>Sketch / diagram (optional)</Label>
            {!showSketch ? (
              <Button type="button" variant="outline" size="sm" data-testid="letter-add-sketch" onClick={() => setShowSketch(true)}><PenLine className="h-4 w-4 mr-1" /> Add sketch</Button>
            ) : (
              <Button type="button" variant="ghost" size="sm" className="text-red-600" data-testid="letter-remove-sketch" onClick={() => { set("sketch", ""); setShowSketch(false); }}>Remove sketch</Button>
            )}
          </div>
          {showSketch && <SketchPad key={editingId || "new"} value={form.sketch} onChange={(v) => set("sketch", v)} />}
        </div>
        <div className="space-y-1.5 max-w-xs"><Label>Closing</Label><Input data-testid="letter-closing" value={form.closing} onChange={(e) => set("closing", e.target.value)} /></div>
        <div className="space-y-2">
          <Label>Page margins (like Microsoft Word)</Label>
          <div className="flex flex-wrap items-end gap-3">
            <div className="w-44 space-y-1">
              <Label className="text-xs text-slate-500">Preset</Label>
              <Select value={marginPreset} onValueChange={applyMarginPreset}>
                <SelectTrigger data-testid="letter-margin-preset"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Normal">Normal (2.54 cm)</SelectItem>
                  <SelectItem value="Narrow">Narrow (1.27 cm)</SelectItem>
                  <SelectItem value="Moderate">Moderate</SelectItem>
                  <SelectItem value="Wide">Wide</SelectItem>
                  <SelectItem value="Custom">Custom</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {[["margin_top", "Top"], ["margin_bottom", "Bottom"], ["margin_left", "Left"], ["margin_right", "Right"]].map(([k, lbl]) => (
              <div key={k} className="w-24 space-y-1">
                <Label className="text-xs text-slate-500">{lbl} (cm)</Label>
                <Input type="number" min="0.5" max="6" step="0.1" data-testid={`letter-${k}`} value={((form[k] ?? 25.4) / 10).toFixed(2)} onChange={(e) => setMargin(k, e.target.value)} />
              </div>
            ))}
          </div>
          <p className="text-xs text-slate-400">Applied to the exported letter PDF. Normal = 2.54 cm (1 inch) all round.</p>
        </div>
        <div className="flex flex-wrap gap-2 pt-1">
          <Button onClick={save} disabled={saving} data-testid="letter-save" className="bg-[#1E3A8A] hover:bg-[#1D4ED8]"><Save className="h-4 w-4 mr-2" /> {editingId ? "Update letter" : "Save letter"}</Button>
          <Button onClick={saveAndExport} disabled={saving} variant="outline" data-testid="letter-save-export"><FileText className="h-4 w-4 mr-2" /> Save & export PDF</Button>
          <Button onClick={saveAndExportDocx} disabled={saving} variant="outline" data-testid="letter-save-export-docx"><FileDown className="h-4 w-4 mr-2" /> Save & export Word</Button>
        </div>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
        <div className="px-5 py-3 border-b border-slate-100 font-semibold text-slate-700">Saved letters</div>
        {isLoading ? <TableSkeleton cols={6} /> : letters.length === 0 ? (
          <EmptyState text="No letters yet. Write one above and click Save." />
        ) : (
          <Table>
            <TableHeader><TableRow>
              <TableHead>Ref</TableHead><TableHead>Subject</TableHead><TableHead>Recipient</TableHead><TableHead>Date</TableHead><TableHead>Last exported</TableHead><TableHead className="text-right">Actions</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {letters.map((l) => (
                <TableRow key={l.id} data-testid="letter-row">
                  <TableCell className="font-medium text-slate-800">{l.ref || l.number}</TableCell>
                  <TableCell className="text-sm text-slate-700">{l.subject || "—"}</TableCell>
                  <TableCell className="text-sm text-slate-600">{l.recipient || "—"}</TableCell>
                  <TableCell className="text-sm text-slate-500">{(l.letter_date || "").slice(0, 10)}</TableCell>
                  <TableCell className="text-sm text-slate-500">{l.last_exported_at ? l.last_exported_at.slice(0, 10) : "—"}</TableCell>
                  <TableCell className="text-right">
                    <div className="stems-actions">
                      <Button size="sm" variant="outline" onClick={() => exportPdf(l)} data-testid={`letter-pdf-${l.id}`} title="Export PDF"><FileText className="h-4 w-4" /></Button>
                      <Button size="sm" variant="outline" onClick={() => exportDocx(l)} data-testid={`letter-docx-${l.id}`} title="Export Word (.docx)"><FileDown className="h-4 w-4" /></Button>
                      <Button size="sm" variant="outline" onClick={() => load(l)} data-testid={`letter-edit-${l.id}`} title="Edit"><Pencil className="h-4 w-4" /></Button>
                      <Confirm onConfirm={() => remove(l.id)} title="Delete letter?" description={`This removes "${l.subject || l.number}".`} trigger={<Button size="sm" variant="outline" className="text-red-600" data-testid={`letter-delete-${l.id}`}><Trash2 className="h-4 w-4" /></Button>} />
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>
    </div>
  );
}
